import type { ComposerUploadLifecycle } from '@/components/org-chat/composer-upload-lifecycle';
import type { OrgChatAttachmentInput } from '@/components/org-chat/org-chat-types';
import type { ReferenceSelectionCategory } from '@/components/org-chat/reference-selection-policy';
import type { ComposerUploadProgress } from '@/lib/uploads/composer-upload-progress';
import type {
  BackgroundUploadJob,
  BackgroundUploadServer,
  BackgroundUploadStore,
  PendingUploadTarget,
} from './background-upload-types';
import {
  createJobReporting,
  type ManagedUploadJob,
} from './background-upload-handoff';
import { createDurableStorageCheck } from './background-upload-durability';
import { createUploadLocks, type UploadLocks } from './background-upload-locks';
import {
  backgroundJobMeta,
  newPersistedUploadRecord,
  progressUpdatesBySend,
  recordsByThread,
  selectResumableRecords,
} from './background-upload-records';
import type { PersistedUpload } from './persisted-upload-record';
import {
  persistableUploadSession,
  type UploadSessionJournal,
} from './upload-session-journal';

export type {
  BackgroundUploadJob,
  BackgroundUploadServer,
  BackgroundUploadStore,
  PendingUploadFailureReason,
  PendingUploadTarget,
} from './background-upload-types';
export { pendingUploadFailureReason } from './background-upload-types';

type Job = ManagedUploadJob;

type Deps = {
  server: BackgroundUploadServer;
  store: BackgroundUploadStore;
  journal: UploadSessionJournal;
  progress: { get: (uploadId: string) => ComposerUploadProgress | undefined };
  /** The app-level lifecycle that runs uploads resumed after a reload. */
  lifecycleFor: (orgSlug: string) => ComposerUploadLifecycle;
  /** Reports a background step that failed; never throws. */
  report?: (step: string, error: unknown) => void;
  /** One tab per upload (Web Locks); without it every claim is granted. */
  locks?: UploadLocks;
};

/**
 * Owns the files a send took with it while they were still uploading
 * (#4844). It lives at app level, so leaving the page never drops them; it
 * keeps a copy of each file in browser storage, so a reload resumes them;
 * and it tells the server how each one is doing. The server starts the run
 * once every file is in. The manager never starts anything itself.
 *
 * It works for one signed-in member at a time (`setActor`): a sign-out or
 * another member's sign-in stops every job first, and only the member's own
 * copies are ever resumed. Each upload belongs to one tab: the one holding
 * its lock, from adoption or resume until the job ends.
 */
export function createBackgroundUploadManager(deps: Deps) {
  const jobs = new Map<string, Job>();
  const locks = deps.locks ?? createUploadLocks(null);
  const listeners = new Set<() => void>();
  let snapshot: BackgroundUploadJob[] = [];
  let actor: string | null = null;
  // Bumped on every member change: async work from an older scope stops.
  let scope = 0;
  const durability = createDurableStorageCheck(deps.store.probeDurableStorage);
  const changed = () => {
    snapshot = [...jobs.values()].map(
      ({
        actorId: _actorId,
        lifecycle: _lifecycle,
        reportedBytes: _reported,
        stored: _stored,
        bound: _bound,
        ...job
      }) => job
    );
    for (const listener of listeners) listener();
  };
  const quietly = (step: string, work: Promise<unknown>) =>
    work.catch((error: unknown) => deps.report?.(step, error));

  // Keep each persisted copy's multipart session current, so a reload
  // resumes by part. Only its id goes to storage, never its token.
  deps.journal.subscribe((uploadId, entry) => {
    const multipart = entry && persistableUploadSession(entry);
    if (!jobs.has(uploadId) || !multipart) return;
    void quietly(
      'persist-session',
      deps.store.updateUpload(uploadId, { multipart })
    );
  });

  /** Delete a copy only when it is the signed-in member's own. */
  const deleteOwnCopy = async (uploadId: string, owner = actor) => {
    const records = await deps.store.listUploads();
    const own = records.find((record) => record.uploadId === uploadId);
    if (owner && own?.actorId === owner)
      await deps.store.deleteUpload(uploadId);
  };

  const forget = (uploadId: string) => {
    jobs.delete(uploadId);
    locks.release(uploadId);
    deps.journal.close(uploadId);
    void quietly('forget', deps.store.deleteUpload(uploadId));
    changed();
  };

  const { handoffFor, complete } = createJobReporting({
    isCurrent: (job) => jobs.get(job.uploadId) === job,
    server: deps.server,
    forget,
    changed,
    quietly,
    report: deps.report,
  });

  const track = (
    lifecycle: ComposerUploadLifecycle,
    meta: Omit<BackgroundUploadJob, 'state'>,
    actorId: string
  ) => {
    const job: Job = {
      ...meta,
      actorId,
      state: 'uploading',
      lifecycle,
      reportedBytes: 0,
      bound: Promise.resolve(true),
    };
    jobs.set(meta.uploadId, job);
    return job;
  };

  return {
    /**
     * The signed-in member (null: signed out). A change stops every job with
     * no report under the new session (the server's lease ends the old
     * waits); the old member's copies stay here for their next sign-in.
     */
    setActor(next: string | null) {
      if (next === actor) return false;
      actor = next;
      scope += 1;
      const stopped = [...jobs.values()];
      jobs.clear();
      locks.releaseAll();
      for (const job of stopped) {
        deps.journal.close(job.uploadId);
        // Its handoff no longer matches a job, so nothing is reported.
        if (job.lifecycle.entryFor(job.uploadId))
          job.lifecycle.remove(job.uploadId);
      }
      changed();
      return true;
    },

    /**
     * Whether a send may leave its files here: only once a probe found this
     * browser can keep them across a reload. Until then sends hold.
     */
    durableStorageAvailable: durability.available,
    probeDurableStorage: durability.probe,

    /**
     * Take a composer upload a send carried off, at the press: the composer
     * may unmount right after, and the upload keeps running in its
     * lifecycle. `target` is the waiting send, usually still being created;
     * nothing is reported until it exists. A failed creation cancels the
     * upload; a send that gives it back (null) returns it to the composer.
     */
    adopt(args: {
      lifecycle: ComposerUploadLifecycle;
      file: File;
      attachment: OrgChatAttachmentInput & { uploadId: string };
      category?: ReferenceSelectionCategory;
      target: PendingUploadTarget | Promise<PendingUploadTarget | null>;
      orgSlug: string;
    }) {
      const { uploadId } = args.attachment;
      const actorId = actor;
      if (!actorId) return false;
      const job = track(
        args.lifecycle,
        {
          uploadId,
          pendingSendId: '',
          orgThreadId: '',
          orgSlug: args.orgSlug,
          filename: args.attachment.filename,
          sizeBytes: args.file.size,
        },
        actorId
      );
      const reclaim = args.lifecycle.handOff(uploadId, handoffFor(job));
      if (!reclaim) {
        jobs.delete(uploadId);
        return false;
      }
      // Held before its copy is stored, so no other tab resumes it.
      const owned = locks.claim(uploadId);
      job.bound = Promise.resolve(args.target).then(
        (target) => {
          if (jobs.get(uploadId) !== job) return false;
          if (!target) {
            // Never reached a delivery: nothing was reported or kept for it,
            // and its multipart session stays with the running upload.
            jobs.delete(uploadId);
            locks.release(uploadId);
            changed();
            reclaim();
            return false;
          }
          job.pendingSendId = target.pendingSendId;
          job.orgThreadId = target.orgThreadId;
          if (target.routeRef) job.routeRef = target.routeRef;
          changed();
          void owned.then(async () => {
            if (jobs.get(uploadId) === job) await persist(job, args);
          });
          return true;
        },
        (error: unknown) => {
          deps.report?.('bind', error);
          if (jobs.get(uploadId) === job) job.lifecycle.remove(uploadId);
          return false;
        }
      );
      changed();
      return true;
    },

    /**
     * After a reload: pick up this workspace's persisted files whose send is
     * still waiting on them and resume each one; drop copies nothing needs.
     */
    async resume(orgSlug: string) {
      const actorId = actor;
      const at = scope;
      if (!actorId) return;
      const listed = await deps.store.listUploads();
      if (at !== scope) return;
      const { resumable, unowned } = selectResumableRecords(
        listed,
        { actorId, orgSlug },
        (uploadId) => jobs.has(uploadId)
      );
      for (const record of unowned)
        void quietly('drop', deps.store.deleteUpload(record.uploadId));
      // Another tab holding an upload's lock owns it: leave it and its copy.
      const owned = await Promise.all(
        resumable.map((record) => locks.claim(record.uploadId))
      );
      const records = resumable.filter((_, index) => owned[index]);
      if (at !== scope) {
        for (const record of records) locks.release(record.uploadId);
        return;
      }
      // Claim them at once: this device holds their bytes, so their message
      // must not say they are on another device while the server is asked.
      const claimed = new Map(
        records.map((record) => [
          record.uploadId,
          track(deps.lifecycleFor(orgSlug), backgroundJobMeta(record), actorId),
        ])
      );
      const release = (uploadId: string) => {
        if (jobs.get(uploadId) !== claimed.get(uploadId)) return;
        jobs.delete(uploadId);
        locks.release(uploadId);
      };
      if (records.length) changed();
      for (const [orgThreadId, threadRecords] of recordsByThread(records)) {
        const pending = await deps.server
          .pendingFiles(orgThreadId)
          .catch(() => undefined);
        // Another member signed in meanwhile: their scope starts clean.
        if (at !== scope) return;
        // Unknown (offline): keep the copies for the next load.
        if (pending === undefined) {
          for (const record of threadRecords) release(record.uploadId);
          changed();
          continue;
        }
        for (const record of threadRecords) {
          // Removed meanwhile (Remove on its row): nothing left to resume.
          if (jobs.get(record.uploadId) !== claimed.get(record.uploadId))
            continue;
          const waiting =
            pending?.pendingSendId === record.pendingSendId &&
            pending.files.some(
              (file) =>
                file.uploadId === record.uploadId &&
                (file.status === 'uploading' || file.status === 'failed')
            );
          if (!waiting) {
            release(record.uploadId);
            changed();
            void quietly('drop', deps.store.deleteUpload(record.uploadId));
            continue;
          }
          await resumeRecord(record, actorId, at);
        }
      }
    },

    retry(uploadId: string) {
      const job = jobs.get(uploadId);
      if (!job) return;
      job.state = 'uploading';
      job.failure = undefined;
      changed();
      if (job.stored) void complete(job);
      else void job.lifecycle.retry(uploadId);
    },

    /** Leave a file out of its message, whether or not its bytes are here. */
    remove(args: { uploadId: string; pendingSendId: string }) {
      const job = jobs.get(args.uploadId);
      // A running upload stops through its lifecycle, which publishes the
      // removal; one still being resumed has no entry there yet.
      if (job && !job.stored && job.lifecycle.entryFor(args.uploadId)) {
        job.lifecycle.remove(args.uploadId);
        return;
      }
      if (job) forget(args.uploadId);
      void quietly('remove', deps.server.remove(args));
      if (!job) void quietly('forget', deleteOwnCopy(args.uploadId));
    },

    /** Send the bytes moved since the last report, one write per send. */
    async flushProgress() {
      const bySend = progressUpdatesBySend(
        jobs.values(),
        (uploadId) => deps.progress.get(uploadId)?.loadedBytes ?? 0
      );
      await Promise.all(
        [...bySend].map(([pendingSendId, updates]) =>
          quietly('progress', deps.server.progress(pendingSendId, updates))
        )
      );
    },

    /** Stall detection for uploads whose composer is gone. */
    sweepStalls() {
      for (const lifecycle of new Set(
        [...jobs.values()].map((job) => job.lifecycle)
      ))
        lifecycle.sweepStalls();
    },

    jobs: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  async function persist(
    job: Job,
    args: {
      file: File;
      attachment: OrgChatAttachmentInput;
      category?: ReferenceSelectionCategory;
    }
  ) {
    const saved = await deps.store
      .persistUpload(
        newPersistedUploadRecord(job, args, deps.journal.get(job.uploadId)),
        args.file
      )
      .catch((error: unknown) => {
        deps.report?.('persist', error);
        return null;
      });
    if (saved && saved.blobStore !== 'none') return;
    // The browser could not keep it: it goes on from this tab only, and the
    // next press holds until a probe finds storage again.
    durability.unavailable();
    deps.report?.('persist-not-durable', new Error('UPLOAD_NOT_DURABLE'));
    if (jobs.get(job.uploadId) !== job) return;
    job.durable = false;
    changed();
  }

  async function resumeRecord(
    record: PersistedUpload,
    actorId: string,
    at: number
  ) {
    const file = await deps.store.readUploadFile(record).catch(() => null);
    if (at !== scope) return;
    if (!file) {
      // The copy is gone (storage cleared, quota): it has to be attached again.
      await quietly(
        'fail',
        deps.server.fail({
          pendingSendId: record.pendingSendId,
          uploadId: record.uploadId,
          reason: 'file_unavailable',
        })
      );
      forget(record.uploadId);
      return;
    }
    // Its id only: the upload asks the server for a fresh capability.
    if (record.multipart) deps.journal.open(record.uploadId, record.multipart);
    const lifecycle = deps.lifecycleFor(record.orgSlug);
    const attachment = {
      uploadId: record.uploadId,
      kind: record.kind,
      filename: record.filename,
      mimeType: record.mimeType,
      sizeBytes: record.sizeBytes,
      isUploading: true,
    };
    const job = track(lifecycle, backgroundJobMeta(record), actorId);
    const handoff = handoffFor(job);
    lifecycle.add({
      file,
      attachment,
      ...(record.referenceCategory
        ? { category: record.referenceCategory as ReferenceSelectionCategory }
        : {}),
      publish: handoff.publish,
    });
    lifecycle.handOff(record.uploadId, handoff);
    changed();
    void lifecycle.run(record.uploadId);
  }
}

export type BackgroundUploadManager = ReturnType<
  typeof createBackgroundUploadManager
>;
